<template>
  <div>
    <valkyrie-list
      v-model="selected"
      :show-select="showSelect"
      item-key="id"
      sort-by="name"
      sortable
      :list-data="propertiesListData"
      :pagination="propertiesListPagination"
      :pagination-text="paginationText"
      :list-name="'Properties'"
      :headers.sync="headers"
      :is-loading="loading"
      @update-pagination="handleUpdatePagination"
      @update-sort="handleUpdateSort"
      @selected-rows="setSelected"
    >
      <template v-slot:list-header>
        <default-list-header
          :searchable="hasDataToQuery"
          :exportable="exportable && hasDataToQuery"
          :filterable="filterable && hasDataToQuery"
          :pagination-text="paginationText"
          @search="handleUpdateSearch"
          @export-csv="exportCSV('Properties')"
          @clear-filters="handleClearFilters"
        >
          <template #status>
            <status-filter
              :items="statusItems"
              @select-status="handleUpdateFilters"
            />
          </template>
          <template #tags>
            <async-multiselect-filter
              filter-key="tag_id"
              :searchable="false"
              :fetch-function="fetchAllPropertyTags"
              @apply-multiselect="handleUpdateFilters"
            />
          </template>
          <template #city>
            <multiselect-filter
              filter-key="city"
              :items="cities"
              @apply-multiselect="handleUpdateFilters"
            />
          </template>
          <template #state>
            <multiselect-filter
              filter-key="region"
              :items="regions"
              @apply-multiselect="handleUpdateFilters"
            />
          </template>
          <template
            v-if="bulkEditing"
            #bulk-editor
          >
            <div class="ml-6 d-flex mt-7">
              <v-menu
                v-for="item of bulkEditButtons"
                :key="item.ID"
                :offset-y="true"
                transition="slide-y-transition"
                bottom
                nudge-bottom="5"
              >
                <template v-slot:activator="{ value }">
                  <div>
                    <v-btn
                      :color="item.color"
                      class="mr-5"
                      :max-width="item.width"
                      :width="item.width"
                      :min-width="item.width"
                    >
                      <v-icon
                        small
                        class="mr-4"
                      >
                        {{ item.icon }}
                      </v-icon>
                      {{ item.text }}
                      <font-awesome-icon
                        v-if="item.text !== 'Delete'"
                        class="ml-4"
                        :icon="value ? 'chevron-up' : 'chevron-down'"
                      />
                    </v-btn>
                  </div>
                </template>
              </v-menu>
            </div>
          </template>
          <template
            v-if="selected.length"
            #number-selected
          >
            <span class="ml-7 mt-4">{{ selected.length }} propert{{ selected.length === 1 ? 'y' : 'ies' }}
              selected</span>
          </template>
        </default-list-header>
      </template>
      <!-- eslint-disable vue/valid-v-slot -->
      <template #item.status="{ item }">
        <v-tooltip
          right
          max-width="322px"
          open-delay="250"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              class="pb-1"
              :color="item.status.color"
              x-small
              v-on="on"
            >
              $faCircle
            </v-icon>
          </template>
          <span>{{ item.status.description }}</span>
        </v-tooltip>
      </template>
      <template #item.name="{ item }">
        <span
          class="property-name font-weight-bold"
          @click="goToProperty(item)"
        >{{ item.name }}</span>
      </template>
      <template #item.tags="{ item }">
        <v-chip
          v-for="(tag, idx) in item.tags"
          :key="idx"
          outlined
          color="#535353"
          class="ma-1 tag"
        >
          {{ tag }}
        </v-chip>
      </template>
      <template #item.region="{item}">
        {{ item.region }}
      </template>
      <template slot="empty-state">
        <empty-state
          icon="$faHomeLight"
          text="No properties found"
        />
      </template>
    </valkyrie-list>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import listControllerMixin from '@/helpers/list-controller-mixin'
  import { dateTimeSquasher } from '@/helpers/lists'
  import ValkyrieList from '@/components/ValkyrieList.vue'
  import DefaultListHeader from '@/components/ValkyrieListDefaultHeader.vue'
  import EmptyState from '../../components/base/EmptyState.vue'
  import AsyncMultiselectFilter from '@/components/filters/AsyncMultiselectFilter.vue'
  import MultiselectFilter from '@/components/filters/MultiselectFilter.vue'
  import StatusFilter from '@/components/filters/StatusFilter.vue'
  import paginationHelper from '@/helpers/pagination-helper'
  import propertyStatusMixin from '@/helpers/property-status-mixin'

  export default {
    name: 'PropertiesList',
    components: { ValkyrieList, DefaultListHeader, EmptyState, AsyncMultiselectFilter, MultiselectFilter, StatusFilter },
    mixins: [listControllerMixin, propertyStatusMixin],
    props: {
      exportable: {
        type: Boolean,
        default: true,
      },
      filterable: {
        type: Boolean,
        default: true,
      },
    },
    data: () => ({
      hasDataToQuery: false,
      bulkEditing: false,
      bulkEditButtons: [
        { text: 'Edit Tags', icon: '$faTag', color: 'primary', width: '157px' },
        { text: 'Edit Thresholds', icon: '$faChartBar', color: 'primary', width: '212px' },
        { text: 'Delete', icon: '$faTrashAlt', color: 'primary', width: '104px' },
      ],
      payload: {
        listType: 'properties',
        fileName: 'properties.csv',
      },
      selected: [],
      headers: [
        {
          text: '',
          value: 'status',
          width: 30,
          align: 'center',
          sortable: false,
        },
        {
          text: 'Property',
          value: 'name',
          width: 200,
        },
        {
          text: 'Address',
          value: 'address',
          width: 225,
          sortable: false,
        },
        {
          text: 'City',
          value: 'city',
          width: 125,
        },
        {
          text: 'State',
          value: 'region',
          width: 110,
        },
        {
          text: 'Tags',
          value: 'tags',
          sortable: false,
        },
      ],
      sorted: true,
      cities: [],
      regions: [],
    }),
    computed: {
      ...mapGetters('Properties', ['total']),
      ...mapGetters({
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
        canManageSelectedOrg: 'Permissions/canManageSelectedOrg',
      }),
      listAPIPath () {
        return `${this.$store.state.baseURL}/properties/list`
      },
      propertiesListData () {
        return this.listData.map((property) => {
          // BE data must be curated and abbreviated due to issues with responsiveness in the properties list
          property = dateTimeSquasher(property)
          let consolidateAddy = `${property.street1}`
          if (property.street2 !== '' && property.street2 !== undefined) consolidateAddy += `, ${property.street2}`
          return {
            ...property,
            address: consolidateAddy,
            region: property.region.toUpperCase(),
            status: this.getPropertyStatus(property.activityZoneCount, property.activeEvents),
          }
        })
      },
      propertiesListPagination () {
        return {
          ...this.pagination,
          total: this.total,
        }
      },
      staticFilters () {
        if (this.currentSelectedOrg.id) {
          return [{ key: 'organization_id', operator: '=', value: `${this.currentSelectedOrg.id}` }]
        }
        return []
      },
      showSelect () {
        return !!(this.currentSelectedOrg.id !== undefined && this.canManageSelectedOrg)
      },
      paginationText () {
        return paginationHelper(this.pagination.pageNum, this.pagination.perPage, this.total)
      },
      filterPayload () {
        return {
          pagination: { perPage: 1000, pageNum: 1 },
          ...(this.currentSelectedOrg.id ? { filter: [{ key: 'organization_id', operator: '=', value: `${this.currentSelectedOrg.id}` }] } : {}),
        }
      },
    },
    watch: {
      currentSelectedOrg () {
        this.initializeList()
      },
      selected (newItems) {
        this.bulkEditing = (newItems.length)
      },
    },
    created () {
      this.defaultSort = [{ key: 'name', order: 'ASC' }]
      this.initializeList()
      this.fetchAllPropertyCities().then(res => { this.cities = res.data.data })
      this.fetchAllPropertyRegions().then(res => { this.regions = res.data.data })
    },
    destroyed () { clearInterval(this.timeout) },
    methods: {
      initializeList () {
        this.fetchList()
          .then((res) => {
            this.updateTotal(res.data.pagination.total)
            this.hasDataToQuery = res.data.pagination.total > 0
          })
      },
      updateTotal (total) {
        this.$store.commit('Properties/updatePaginationTotal', { total })
      },
      fetchAllPropertyTags (payload) {
        return this.$store.dispatch('Properties/fetchPropertyListFilters', { key: 'tags', ...this.filterPayload, ...payload })
      },
      fetchAllPropertyCities () {
        return this.$store.dispatch('Properties/fetchPropertyListFilters', { key: 'city', ...this.filterPayload })
      },
      fetchAllPropertyRegions () {
        return this.$store.dispatch('Properties/fetchPropertyListFilters', { key: 'region', ...this.filterPayload })
      },
      setSelected (items) {
        this.selected = items
      },
      goToProperty (item) {
        if (this.detail) return
        const checkRoute = this.$router.resolve({ path: `/properties/${item.id}` })
        if (checkRoute.resolved.name !== '404 Error') {
          this.$router.push({ path: `/properties/${item.id}` })
        }
      },
    },
  }
</script>
<style lang="scss">
.property-name {
  cursor: pointer;
  color: $muted;
  &:hover {
    text-decoration: underline;
    color:#4C6489;
  }
}
.tag {
  border-color: #535353;
  height:24px !important;
  .v-chip__content {
    font-size: 14px;
  }
}
</style>
